import { get } from 'lodash';
import type { AnalysisRecord } from '../config/types';

const DURATION_BUCKETS = [
  { maxSeconds: 60, label: '< 1 min' },
  { maxSeconds: 300, label: '1-5 min' },
  { maxSeconds: Infinity, label: '> 5 min' },
] as const;

function getDurationBucket(durationSeconds: number): string {
  for (const bucket of DURATION_BUCKETS) {
    if (durationSeconds < bucket.maxSeconds) return bucket.label;
  }
  return DURATION_BUCKETS[DURATION_BUCKETS.length - 1].label;
}

function extractDurationBuckets(calls: AnalysisRecord[]): AnalysisRecord[] {
  const result: AnalysisRecord[] = [];
  calls.forEach((call) => {
    const startedAt = get(call, 'startedAt');
    const endedAt = get(call, 'endedAt');
    if (typeof startedAt !== 'string' || typeof endedAt !== 'string') return;
    const durationMs = new Date(endedAt).getTime() - new Date(startedAt).getTime();
    if (durationMs <= 0 || !Number.isFinite(durationMs)) return;
    const durationSeconds = durationMs / 1000;
    result.push({ durationBucket: getDurationBucket(durationSeconds) });
  });
  return result;
}

function extractSentiments(calls: AnalysisRecord[]): AnalysisRecord[] {
  const result: AnalysisRecord[] = [];
  calls.forEach((call) => {
    const sentiments = get(call, 'sentiments') as
      | { sentimentScores?: { label: string; score: number }[] }[]
      | undefined;
    if (!sentiments?.length) return;
    const last = sentiments[sentiments.length - 1];
    const scores = last?.sentimentScores;
    if (!scores?.length) return;
    const dominant = scores.reduce((best, cur) => (cur.score > best.score ? cur : best), scores[0]);
    result.push({ sentiment: dominant.label });
  });
  return result;
}

const mappers: Record<string, (data: AnalysisRecord[]) => AnalysisRecord[]> = {
  extractSentiments,
  extractDurationBuckets,
};

export function applyMapper(
  data: AnalysisRecord[],
  mapperName: string | undefined,
): AnalysisRecord[] {
  if (!mapperName) return data;
  const fn = mappers[mapperName];
  return fn ? fn(data) : data;
}
